import {
  BarChart3,
  Bell,
  Bookmark,
  BookUser,
  Briefcase,
  Building,
  Handshake,
  Home,
  LayoutDashboard,
  List,
  Mail,
  MousePointerClick,
  Search,
  Send,
  Settings,
  SlidersHorizontal,
  UserPlus,
  Users,
} from "lucide-react";
import { type FC, type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hasLicense, LICENSE_KEY } from "../../constants/licenses";
import type { CurrentUser } from "../../hooks/useAuthRestApi";
import { cn } from "../../lib/utils";
import { canAccessAdminPages } from "../../utils/admin-utils";
import { getCurrentTenant, isTenantAdmin } from "../../utils/authority-utils";
import { DropdownMenuUI } from "../DropdownMenu";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

interface SideBarMenuProps {
  currentUser?: CurrentUser | null;
  currentPath: string;
  isSidebarOpen?: boolean;
}

interface MenuItem {
  id: string;
  title: string;
  to: string;
  icon?: ReactNode;
}

interface MenuSection {
  id: string;
  label: string;
  icon: ReactNode;
  /** メインリンク（セクションヘッダー自体がリンクになる場合） */
  to?: string;
  children: MenuItem[];
  display: boolean;
}

export const SideBarMenuUI: FC<SideBarMenuProps> = ({
  currentUser,
  currentPath,
  isSidebarOpen = false,
}) => {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [isHomeHovered, setIsHomeHovered] = useState(false);
  const [homeTooltipPosition, setHomeTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isUserHovered, setIsUserHovered] = useState(false);
  const [userTooltipPosition, setUserTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const tenant = getCurrentTenant(currentUser ?? null);
  const licenses = tenant?.licenses;
  const isAdmin = isTenantAdmin(currentUser ?? null);

  const onClickUser = () => {
    setShowDropdownMenu(true);
  };

  const onHiddenDropdownMenu = () => {
    setShowDropdownMenu(false);
  };

  const handleHomeMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHomeTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsHomeHovered(true);
  };

  const handleUserMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setUserTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsUserHovered(true);
  };

  const sections: MenuSection[] = useMemo(
    () => [
      {
        id: "search",
        label: "メール検索",
        icon: <Search className="w-4 h-4" />,
        to: "/emails/search",
        children: [],
        display: hasLicense(licenses, LICENSE_KEY.EMAIL_SEARCH),
      },
      {
        id: "matching",
        label: "マッチング",
        icon: <Handshake className="w-4 h-4" />,
        children: [
          {
            id: "matchingResults",
            title: "結果一覧",
            to: "/matching/results",
            icon: <List className="w-3.5 h-3.5" />,
          },
          {
            id: "matchingPresets",
            title: "プリセット",
            to: "/settings/presets",
            icon: <Bookmark className="w-3.5 h-3.5" />,
          },
          {
            id: "matchingFree",
            title: "フリー",
            to: "/matching/free",
            icon: <MousePointerClick className="w-3.5 h-3.5" />,
          },
          {
            id: "matchingConditions",
            title: "条件設定",
            to: "/settings/matching/conditions",
            icon: <SlidersHorizontal className="w-3.5 h-3.5" />,
          },
        ],
        display: hasLicense(licenses, LICENSE_KEY.MATCHING),
      },
      {
        id: "crm-talents",
        label: "タレント管理",
        icon: <Users className="w-4 h-4" />,
        to: "/crm/talents",
        children: [],
        display: hasLicense(licenses, LICENSE_KEY.CRM),
      },
      {
        id: "crm-sales-board",
        label: "営業ボード",
        icon: <LayoutDashboard className="w-4 h-4" />,
        to: "/crm/sales-board",
        children: [
          {
            id: "salesPipeline",
            title: "パイプライン",
            to: "/crm/sales-board",
            icon: <Briefcase className="w-3.5 h-3.5" />,
          },
          {
            id: "crmNotifications",
            title: "通知設定",
            to: "/crm/notification-settings",
            icon: <Bell className="w-3.5 h-3.5" />,
          },
        ],
        display: hasLicense(licenses, LICENSE_KEY.CRM),
      },
      {
        id: "delivery",
        label: "メール一斉配信",
        icon: <Send className="w-4 h-4" />,
        to: "/deliveries",
        children: [
          {
            id: "distributionGroups",
            title: "配信グループ",
            to: "/admin/distribution/groups",
            icon: <Users className="w-3.5 h-3.5" />,
          },
          {
            id: "distributionContacts",
            title: "連絡先管理",
            to: "/admin/distribution/contacts",
            icon: <BookUser className="w-3.5 h-3.5" />,
          },
        ],
        display: hasLicense(licenses, LICENSE_KEY.BULK_DELIVERY),
      },
      {
        id: "usage",
        label: "利用状況",
        icon: <BarChart3 className="w-4 h-4" />,
        to: "/usage",
        children: [],
        display: true,
      },
      {
        id: "settings",
        label: "設定",
        icon: <Settings className="w-4 h-4" />,
        children: [
          {
            id: "tenantManagement",
            title: "テナント管理",
            to: "/settings/tenant",
            icon: <Building className="w-3.5 h-3.5" />,
          },
          {
            id: "invitations",
            title: "メンバー招待",
            to: "/settings/invitations",
            icon: <UserPlus className="w-3.5 h-3.5" />,
          },
          {
            id: "senderEmails",
            title: "送信者メール管理",
            to: "/settings/sender-emails",
            icon: <Mail className="w-3.5 h-3.5" />,
          },
        ],
        display: isAdmin,
      },
      {
        id: "admin",
        label: "管理画面",
        icon: <Settings className="w-4 h-4" />,
        to: "/admin",
        children: [],
        display: canAccessAdminPages(currentUser ?? null),
      },
    ],
    [licenses, isAdmin, currentUser],
  );

  const visibleSections = sections.filter(s => s.display);

  return (
    <div className="flex flex-col h-full px-3">
      {/* ホーム */}
      <div className="relative">
        <Link
          to="/dashboard"
          className={cn(
            "flex items-center space-x-2 px-2 py-3 mb-2 rounded-lg transition-colors",
            currentPath === "/dashboard"
              ? "bg-white/20 text-white"
              : "text-white/80 hover:bg-white/10 hover:text-white",
          )}
          onMouseEnter={!isSidebarOpen ? handleHomeMouseEnter : undefined}
          onMouseLeave={
            !isSidebarOpen ? () => setIsHomeHovered(false) : undefined
          }
        >
          <Home className="w-5 h-5 shrink-0" />
          {isSidebarOpen && (
            <span className="font-semibold text-sm">ホーム</span>
          )}
        </Link>
        {!isSidebarOpen && isHomeHovered && (
          <div
            className="fixed text-xs whitespace-nowrap pointer-events-none px-2.5 py-1 bg-gradient-to-r from-sidebar-left to-sidebar-right text-white rounded-lg shadow-lg border border-white/20 z-50"
            style={{
              top: `${homeTooltipPosition.top}px`,
              left: `${homeTooltipPosition.left}px`,
              transform: "translateY(-50%)",
            }}
          >
            ホーム
          </div>
        )}
      </div>

      <Separator className="mb-3 bg-white/10" />

      {/* メニューセクション */}
      <nav className="flex-1 overflow-y-auto space-y-4">
        {visibleSections.map(section => (
          <SideBarSection
            key={section.id}
            section={section}
            currentPath={currentPath}
            isSidebarOpen={isSidebarOpen}
          />
        ))}
      </nav>

      {/* ユーザー */}
      {currentUser && (
        <div className="relative shrink-0 pb-4 pt-3 border-t border-white/10 mt-3">
          <div
            className="flex flex-row items-center space-x-2 cursor-pointer px-2 py-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={onClickUser}
            onMouseEnter={!isSidebarOpen ? handleUserMouseEnter : undefined}
            onMouseLeave={
              !isSidebarOpen ? () => setIsUserHovered(false) : undefined
            }
          >
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-medium shrink-0">
              {currentUser.familyName?.charAt(0) || "U"}
            </div>
            {isSidebarOpen && (
              <span
                className="text-sm text-white truncate"
                data-testid="sidebar-username"
              >
                {currentUser.familyName} {currentUser.givenName}
              </span>
            )}
          </div>
          {!isSidebarOpen && isUserHovered && !showDropdownMenu && (
            <div
              className="fixed text-xs whitespace-nowrap pointer-events-none px-2.5 py-1 bg-gradient-to-r from-sidebar-left to-sidebar-right text-white rounded-lg shadow-lg border border-white/20 z-50"
              style={{
                top: `${userTooltipPosition.top}px`,
                left: `${userTooltipPosition.left}px`,
                transform: "translateY(-50%)",
              }}
            >
              {currentUser.familyName} {currentUser.givenName}
            </div>
          )}
          <DropdownMenuUI
            visibility={showDropdownMenu}
            onHidden={onHiddenDropdownMenu}
          />
        </div>
      )}
    </div>
  );
};

// ===============================
// セクションコンポーネント
// ===============================

interface SideBarSectionProps {
  section: MenuSection;
  currentPath: string;
  isSidebarOpen: boolean;
}

const SideBarSection: FC<SideBarSectionProps> = ({
  section,
  currentPath,
  isSidebarOpen,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsHovered(true);
  };

  // セクションヘッダー（リンクまたはラベル）
  const headerContent = (
    <>
      <span
        className={cn(
          "transition-transform duration-200",
          isSidebarOpen && "mr-2.5",
        )}
      >
        {section.icon}
      </span>
      {isSidebarOpen && (
        <span className="text-xs font-semibold uppercase tracking-wider">
          {section.label}
        </span>
      )}
    </>
  );

  return (
    <div>
      {/* セクションヘッダー */}
      <div className="relative">
        {section.to ? (
          <Link
            to={section.to}
            className={cn(
              "flex items-center px-3 py-2 rounded-lg transition-all duration-200",
              isSidebarOpen ? "justify-start" : "justify-center",
              currentPath === section.to
                ? "bg-white/15 text-white"
                : "text-white/60 hover:text-white hover:bg-white/10",
            )}
            onMouseEnter={!isSidebarOpen ? handleMouseEnter : undefined}
            onMouseLeave={
              !isSidebarOpen ? () => setIsHovered(false) : undefined
            }
          >
            {headerContent}
          </Link>
        ) : (
          <div
            className={cn(
              "flex items-center px-3 py-2",
              isSidebarOpen ? "justify-start" : "justify-center",
              "text-white/50",
            )}
            onMouseEnter={!isSidebarOpen ? handleMouseEnter : undefined}
            onMouseLeave={
              !isSidebarOpen ? () => setIsHovered(false) : undefined
            }
          >
            {headerContent}
          </div>
        )}
        {!isSidebarOpen && isHovered && (
          <div
            className="fixed text-xs whitespace-nowrap pointer-events-none px-2.5 py-1 bg-gradient-to-r from-sidebar-left to-sidebar-right text-white rounded-lg shadow-lg border border-white/20 z-50"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateY(-50%)",
            }}
          >
            {section.label}
          </div>
        )}
      </div>

      {/* 子メニュー */}
      {section.children.length > 0 && isSidebarOpen && (
        <ul className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-0.5">
          {section.children.map(child => (
            <SideBarChildItem
              key={child.id}
              item={child}
              isActive={currentPath === child.to}
              isSidebarOpen={isSidebarOpen}
            />
          ))}
        </ul>
      )}

      {/* サイドバー閉じてるとき：子アイテムをアイコンだけで表示 */}
      {section.children.length > 0 && !isSidebarOpen && (
        <ul className="mt-0.5 space-y-0.5">
          {section.children.map(child => (
            <SideBarChildItem
              key={child.id}
              item={child}
              isActive={currentPath === child.to}
              isSidebarOpen={isSidebarOpen}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

// ===============================
// 子メニューアイテム
// ===============================

interface SideBarChildItemProps {
  item: MenuItem;
  isActive: boolean;
  isSidebarOpen: boolean;
}

const SideBarChildItem: FC<SideBarChildItemProps> = ({
  item,
  isActive,
  isSidebarOpen,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
    setIsHovered(true);
  };

  return (
    <li>
      <div className="relative">
        <Button
          asChild
          variant="ghost"
          className={cn(
            "w-full text-white/70 hover:bg-white/10 hover:text-white",
            isSidebarOpen ? "justify-start" : "justify-center",
            "transition-all duration-200 ease-in-out",
            "h-8 px-2.5 rounded-md text-xs",
            "hover:translate-x-0.5",
            isActive && "bg-white/10 text-white font-medium",
          )}
        >
          <Link
            to={item.to}
            className={cn(
              "flex items-center",
              isSidebarOpen ? "justify-start" : "justify-center",
            )}
            onMouseEnter={!isSidebarOpen ? handleMouseEnter : undefined}
            onMouseLeave={
              !isSidebarOpen ? () => setIsHovered(false) : undefined
            }
          >
            {item.icon && (
              <span className={cn("opacity-70", isSidebarOpen && "mr-2.5")}>
                {item.icon}
              </span>
            )}
            {isSidebarOpen && (
              <span className="whitespace-nowrap">{item.title}</span>
            )}
          </Link>
        </Button>
        {!isSidebarOpen && isHovered && (
          <div
            className="fixed text-xs whitespace-nowrap pointer-events-none px-2.5 py-1 bg-gradient-to-r from-sidebar-left to-sidebar-right text-white rounded-lg shadow-lg border border-white/20 z-50"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateY(-50%)",
            }}
          >
            {item.title}
          </div>
        )}
      </div>
    </li>
  );
};
